import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../services/api'
import SelecaoColaboradores, { selecaoColaboradoresVazia, SelecaoColaboradoresValor } from '../components/SelecaoColaboradores'

interface Resultado {
  total: number
  criadas: number
  jaExistentes: number
  semDissidio: { motcod: string; motnome: string }[]
  erros: { motcod: string; motnome: string; erro: string }[]
}

export default function AbrirFichasLote() {
  const navigate = useNavigate()
  const [selecao, setSelecao] = useState<SelecaoColaboradoresValor>(selecaoColaboradoresVazia())
  const [ano, setAno] = useState(new Date().getFullYear())
  const [mes, setMes] = useState(new Date().getMonth() + 1)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [resultado, setResultado] = useState<Resultado | null>(null)
  const [periodoMisto, setPeriodoMisto] = useState(false)

  // O período (dias — de/até) é sugerido a partir do parâmetro cadastrado pra empresa e
  // dissídio de cada colaborador (dia_inicio_periodo) — recalcula sempre que a seleção,
  // o ano ou o mês mudam. Se os colaboradores selecionados tiverem períodos diferentes
  // configurados, avisa (o palpite usa o mais comum entre eles) — o digitador ainda pode
  // ajustar as datas manualmente antes de abrir.
  useEffect(() => {
    if (selecao.modo === 'individual' && selecao.colaboradoresIndividuais.length === 0) return
    if (selecao.modo === 'categoria' && selecao.catcat.length === 0) return
    const params: Record<string, string | number> = { ano, mes, modo: selecao.modo }
    if (selecao.modo === 'categoria') params.catcat = selecao.catcat.join(',')
    if (selecao.modo === 'individual') params.colaboradores = selecao.colaboradoresIndividuais.map(c => c.motcod).join(',')
    if (selecao.excluidos.length > 0) params.excluidos = selecao.excluidos.join(',')
    api.get('/cartao-ponto/periodo-sugerido-lote', { params }).then(r => {
      setDataInicio(r.data.dataInicio)
      setDataFim(r.data.dataFim)
      setPeriodoMisto(Boolean(r.data.misto))
    })
  }, [selecao.modo, selecao.catcat, selecao.colaboradoresIndividuais, selecao.excluidos, ano, mes])

  async function salvar() {
    if (!dataInicio || !dataFim) { setErro('Informe o período de dias.'); return }
    if (selecao.modo === 'individual' && selecao.colaboradoresIndividuais.length === 0) { setErro('Selecione ao menos um colaborador.'); return }
    if (selecao.modo === 'categoria' && selecao.catcat.length === 0) { setErro('Selecione ao menos uma categoria de cargo.'); return }

    setSalvando(true)
    setErro('')
    setResultado(null)
    try {
      const r = await api.post('/cartao-ponto/fichas/lote', {
        modo: selecao.modo, catcat: selecao.catcat, colaboradores: selecao.colaboradoresIndividuais.map(c => c.motcod),
        excluidos: selecao.excluidos,
        ano, mes, dataInicio, dataFim,
      })
      setResultado(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao abrir as fichas.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div style={{ maxWidth: 780 }}>
      <div className="card">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.4rem' }}>Abrir Fichas em Lote</h3>
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 0, marginBottom: '1rem' }}>
          Empresa e dissídio vêm do cadastro de cada colaborador (Funcionários {'>'} Configurações {'>'} Cadastro de Colaboradores) —
          quem não tiver dissídio cadastrado é listado no resultado, mas não tem a ficha aberta.
        </p>

        <SelecaoColaboradores valor={selecao} onChange={setSelecao} />

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1.2rem' }}>
          <div className="field">
            <label>Ano (período) *</label>
            <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))} />
          </div>

          <div className="field">
            <label>Mês (período) *</label>
            <select value={mes} onChange={e => setMes(Number(e.target.value))}>
              {['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
                .map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Dias — de *</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </div>

          <div className="field">
            <label>Dias — até *</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </div>
        </div>

        {periodoMisto && (
          <div style={{ color: '#b45309', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            Os colaboradores selecionados têm períodos diferentes configurados — as datas acima seguem o período mais comum entre eles. Confira antes de abrir.
          </div>
        )}

        {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>}

        {resultado && (
          <div style={{ marginTop: '1rem', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '0.85rem' }}>
            <div><b>{resultado.criadas}</b> ficha(s) criada(s) de <b>{resultado.total}</b> colaborador(es) selecionado(s).</div>
            {resultado.jaExistentes > 0 && <div>{resultado.jaExistentes} já tinham ficha nesse período (não alteradas).</div>}
            {resultado.semDissidio.length > 0 && (
              <div style={{ marginTop: 6, color: '#b45309' }}>
                {resultado.semDissidio.length} colaborador(es) sem dissídio cadastrado (ficha NÃO aberta) — corrija em{' '}
                <Link to="/funcionarios/configuracoes/colaboradores">Cadastro de Colaboradores</Link>:
                <ul style={{ margin: '4px 0 0 18px' }}>
                  {resultado.semDissidio.map(c => <li key={c.motcod}>{c.motcod} - {c.motnome}</li>)}
                </ul>
              </div>
            )}
            {resultado.erros.length > 0 && (
              <div style={{ marginTop: 6, color: '#b91c1c' }}>
                {resultado.erros.length} erro(s):
                <ul style={{ margin: '4px 0 0 18px' }}>
                  {resultado.erros.map(e => <li key={e.motcod}>{e.motcod} - {e.motnome}: {e.erro}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Abrindo...' : 'Abrir Fichas'}
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto')}>Voltar</button>
        </div>
      </div>
    </div>
  )
}
